import { withSyncLane } from "./sync-lane.js";
import { clearSyncMetadata } from "./sync-metadata.js";
import { clearThemeSyncCursor } from "../features/theme/theme-metadata.js";

/**
 * Account-scoped sync state: the generation counter every per-account cache is
 * stamped with, plus the transition primitive that prevents an account switch
 * from bisecting a server or Google reconcile pass.
 *
 * Account transitions take the same lane as both sync engines. That makes the
 * transition atomic with respect to their durable writes: an existing pass
 * finishes before the account changes, and a later pass sees the new generation
 * plus cleared cursor/ack/theme state. A dead refresh token discovered by the
 * server client is the one special case: that callback already owns the lane,
 * so its caller clears the state directly and the server pass unwinds.
 */

let generation = 0;

/** Which account's sync state is current. Anything cached per account (pull
 * cursor, "synced this session", first-pass ordering) is valid only while this
 * matches the value it was stamped with. */
export function accountGeneration(): number {
  return generation;
}

/** Invalidate every per-account cache. Call only while the sync lane is held. */
export function bumpAccountGeneration(): void {
  generation++;
}

/** Serialize a sign-in/sign-out/account-switch transition against both sync
 * engines. Cleanup errors propagate; a failed wipe must never be reported as
 * complete because the next account cannot safely reuse its cursor or acks. */
export function withAccountTransition<T>(fn: () => Promise<T>): Promise<T> {
  return withSyncLane(fn);
}

/** Forget the current account's pull cursor, display stamp, preference and row
 * acknowledgements, and theme agreed-base. The caller must hold the sync lane. */
export async function clearAccountSyncState(): Promise<void> {
  await clearSyncMetadata();
  await clearThemeSyncCursor();
}
